import { useMutation } from '@apollo/client';
import { Button, List } from '@material-ui/core';
import React from 'react';
import { hasRole } from 'shared/models/user';

import { Notification, UPDATE_NOTIFICATION } from '../../graphql/queries/notifications';
import { FeesProvider } from '../../hooks/use-fees';
import { useNotificationsData } from '../../hooks/use-notifications-data';
import { useSession } from '../../hooks/use-session';
import { useSnackbar } from '../../hooks/use-snackbar';
import { BankTransactionNotification } from './bank-transaction-notification';
import { CommissionProcessedNotification } from './commissions-processed-notification';
import { FeeCreatedNotification } from './fee-created-notification';
import { HomePagePanel } from './home-page-panel';
import { HTMLProfileParserCheckNotification } from './html-parser-issues-notification';
import { InvoicePaymentNotification } from './invoice-payment-notification';
import { InvoiceStatusChangeNotification } from './invoice-status-change-notification';
import { JDLLMUpdate } from './jd-llm-update';
import { ProfileMergeValidationCheckNotification } from './profile-merge-validation-notification';
import { ScoreGeneratedNotification } from './score-generated-notification';
import { UpdateDraftNotification } from './update-draft-notification';

const Content: React.FC<{ rows: JSX.Element[] }> = ({ rows }) => <List disablePadding={true}>{rows}</List>;

const NotificationComponent: React.FC<{ data: Notification; list: Notification[] }> = ({ data }) => {
    const [markedComplete, setMarkedComplete] = React.useState(false);
    const { setSnackbar } = useSnackbar();
    const [updateNotification] = useMutation<{}, { id: string; updates: { completedAt: null | number } }>(
        UPDATE_NOTIFICATION
    );
    const { user, userPermissions } = useSession();
    const { refetch } = useNotificationsData();

    const handleUndo = async () => {
        setMarkedComplete(false);
        setSnackbar('Notification marked as incomplete');
        await updateNotification({ variables: { id: data.id, updates: { completedAt: null } } });
    };

    const handleMarkComplete = async () => {
        const undoButton = (
            <Button onClick={handleUndo} style={{ color: 'white' }}>
                Undo
            </Button>
        );
        setSnackbar('Notification marked as complete', undoButton);
        setMarkedComplete(true);
        await updateNotification({ variables: { id: data.id, updates: { completedAt: Date.now() } } });
    };

    switch (data.type) {
        case 'JDLLMUpdate':
            return markedComplete ? null : (
                <JDLLMUpdate
                    key={data.id}
                    data={data.jdLLMUpdate[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                />
            );
        case 'FeeCreated': {
            const fee = data.fee[0];
            const canEdit =
                hasRole(userPermissions, 'billing_admin') ||
                (fee.accountManager?.id === user.id && hasRole(userPermissions, 'account_manager'));
            return markedComplete ? null : (
                <FeesProvider key={data.id} data={[fee]} disabled={!canEdit} refetch={refetch.notifications}>
                    <FeeCreatedNotification userId={data.userId} onComplete={handleMarkComplete} fee={fee} />
                </FeesProvider>
            );
        }
        case 'InvoicePaymentRecorded':
            return markedComplete ? null : (
                <InvoicePaymentNotification
                    invoicePayment={data.invoicePayment[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                    createdAt={data.createdAt}
                />
            );
        case 'BankTransactionNotification':
            return markedComplete ? null : (
                <BankTransactionNotification
                    bankTransaction={data.bankTransaction[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                    createdAt={data.createdAt}
                />
            );
        case 'InvoiceStatusChange':
            return markedComplete ? null : (
                <InvoiceStatusChangeNotification
                    invoice={data.invoice[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                    createdAt={data.createdAt}
                    oldStatus={data.data.oldStatus}
                    newStatus={data.data.newStatus}
                />
            );
        case 'CommissionProcessed':
            return markedComplete ? null : (
                <CommissionProcessedNotification
                    commissions={data.commissions}
                    processedAt={data.data.processedAt}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                />
            );
        case 'CandidateScoresGenerated':
            return markedComplete ? null : (
                <ScoreGeneratedNotification
                    person={data.scoredPerson[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                    createdAt={data.createdAt}
                />
            );
        case 'HTMLProfileParserCheck':
            return markedComplete ? null : (
                <HTMLProfileParserCheckNotification
                    userId={data.userId}
                    data={data.data}
                    onComplete={handleMarkComplete}
                    createdAt={data.createdAt}
                />
            );
        case 'ProfileMergeValidationCheck':
            return markedComplete ? null : (
                <ProfileMergeValidationCheckNotification
                    userId={data.userId}
                    data={data.data}
                    onComplete={handleMarkComplete}
                    createdAt={data.createdAt}
                />
            );
        case 'JobUpdateDraft':
            return markedComplete ? null : (
                <UpdateDraftNotification
                    draftType="job"
                    draft={data.jobUpdateDraft[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                />
            );
        case 'ClientUpdateDraft':
            return markedComplete ? null : (
                <UpdateDraftNotification
                    draftType="client"
                    draft={data.clientUpdateDraft[0]}
                    userId={data.userId}
                    onComplete={handleMarkComplete}
                />
            );
        default:
            return null;
    }
};

export const UserNotifications: React.FC = () => {
    const { notifications } = useNotificationsData();
    if (!notifications?.length) {
        return null;
    }

    return (
        <HomePagePanel
            RowComponent={NotificationComponent}
            ContentComponent={Content}
            records={notifications}
            title="Notifications"
            containerClass="list"
            collapseKeyId="user-notifications"
        />
    );
};
